import { useCallback, useEffect, useState } from "react";
import { Bell, Loader2, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoStatusBadge } from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import {
  NOT_APPLICABLE_TASK_COMMENTS,
  TASK_ADVANCE_OPTIONS,
  taskStatusLabel,
  taskStatusVariant,
} from "@/modules/despacho/operationalTaskHelpers";
import InternalReminderDialog, { useEntityAlerts } from "@/pages/despacho/InternalReminderDialog";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";

export default function TaskDetailDialog({
  taskId,
  open,
  onOpenChange,
  onUpdated,
  readOnly = false,
}) {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docKinds, setDocKinds] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docKind, setDocKind] = useState("papeleria");
  const [docTitle, setDocTitle] = useState("");
  const [reminderOpen, setReminderOpen] = useState(false);
  const { alerts, lastSent } = useEntityAlerts("task", taskId);

  const loadDetail = useCallback(async () => {
    if (!taskId || !open) return;
    setLoadState("loading");
    try {
      const [hist, data, docs, kinds] = await Promise.all([
        despachoFiscalInfoApi.getTaskHistory(taskId),
        despachoFiscalInfoApi.getTask(taskId),
        despachoFiscalInfoApi.listTaskDocuments(taskId),
        despachoFiscalInfoApi.listTaskDocumentKinds(),
      ]);
      setDetail(data);
      setHistory(hist);
      setDocuments(docs || []);
      setDocKinds(kinds || []);
      syncWorkClientFromDespacho(data?.fiscal_client_id);
      setComment("");
      setLoadState("ready");
    } catch (err) {
      setDetail(null);
      setHistory([]);
      setDocuments([]);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [taskId, open]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const item = detail?.item;

  const patchStatus = async (status, extraComment = "") => {
    if (readOnly || !taskId) return;
    setBusy(true);
    try {
      const result = await despachoFiscalInfoApi.patchTask(taskId, {
        status,
        comment: extraComment || comment || undefined,
      });
      toast.success("Tarea actualizada.");
      if (result?.suggested_instance_status) {
        toast.message(
          `Sugerencia instancia fiscal: ${taskStatusLabel(result.suggested_instance_status)}`,
        );
      }
      onUpdated?.(result?.item);
      await loadDetail();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (file) => {
    if (readOnly || !taskId || !file) return;
    setUploading(true);
    try {
      await despachoFiscalInfoApi.uploadTaskDocument(taskId, {
        file,
        docKind,
        title: docTitle || file.name,
        readyForClient: true,
      });
      toast.success(
        "Documento cargado. Disponible en Recordatorios → Mensajes generales para vincular al envío.",
      );
      setDocTitle("");
      await loadDetail();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (readOnly || !docId) return;
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await despachoFiscalInfoApi.deleteTaskDocument(docId);
      toast.success("Documento eliminado.");
      await loadDetail();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalle de tarea</DialogTitle>
          <DialogDescription>
            Tarea operativa. Aquí carga documentos para el cliente (declaración, papelería, EEFF, etc.).
          </DialogDescription>
        </DialogHeader>

        {loadState === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando…
          </div>
        ) : null}

        {loadState === "ready" && item ? (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500 block">Cliente</span>
                <strong>{item.client_name || "—"}</strong>
              </div>
              <div>
                <span className="text-slate-500 block">RFC</span>
                <span>{item.client_rfc || "—"}</span>
              </div>
              <div className="col-span-2">
                <span className="text-slate-500 block">Tarea</span>
                <strong>{item.task_name || item.title}</strong>
              </div>
              <div>
                <span className="text-slate-500 block">Obligación</span>
                <span>{item.obligation_name || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Periodo</span>
                <span>{item.period || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Fecha tarea</span>
                <span>{item.due_date || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Fecha fiscal</span>
                <span>{item.fecha_fiscal || "—"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Auxiliar</span>
                <span>{item.assigned_to_name || "Sin asignar"}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Estado</span>
                <DespachoStatusBadge variant={taskStatusVariant(item.status)}>
                  {item.status_label || taskStatusLabel(item.status)}
                </DespachoStatusBadge>
              </div>
              <div>
                <span className="text-slate-500 block">Fuente</span>
                <span>{item.source_label || "—"}</span>
              </div>
            </div>

            <div className="border-t pt-3 space-y-2" data-testid="task-client-documents">
              <p className="font-medium m-0">Documentos para el cliente</p>
              <p className="text-xs text-slate-500 m-0">
                Auxiliar: solo sus tareas. Dueño/comprador: todas. Vincule el envío en Recordatorios → Mensajes
                generales.
              </p>
              {documents.length ? (
                <ul className="space-y-1 text-xs border border-slate-200 rounded-sm p-2 max-h-36 overflow-y-auto">
                  {documents.map((d) => (
                    <li key={d.id} className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <a
                          className="text-[#0F2D5C] underline font-medium"
                          href={despachoFiscalInfoApi.taskDocumentDownloadUrl(d.id)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {d.title || d.filename}
                        </a>
                        <div className="text-slate-500">
                          {d.doc_kind_label}
                          {d.ready_for_client ? " · listo para envío" : " · no listo"}
                        </div>
                      </div>
                      {!readOnly ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-red-700 shrink-0"
                          onClick={() => handleDeleteDoc(d.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500 m-0">Aún no hay documentos en esta tarea.</p>
              )}
              {!readOnly ? (
                <div className="space-y-2 rounded-sm border border-slate-200 p-2 bg-slate-50">
                  <div>
                    <Label className="text-xs">Tipo de documento</Label>
                    <Select value={docKind} onValueChange={setDocKind}>
                      <SelectTrigger className="mt-1 h-8 rounded-sm text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(docKinds.length ? docKinds : [{ id: "otro", label: "Otro" }]).map((k) => (
                          <SelectItem key={k.id} value={k.id}>
                            {k.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Título (opcional)</Label>
                    <Input
                      className="mt-1 h-8 text-xs"
                      value={docTitle}
                      onChange={(e) => setDocTitle(e.target.value)}
                      placeholder="Ej. Papelería febrero"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Archivo (PDF / imagen / Excel)</Label>
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls"
                      className="mt-1 text-xs"
                      disabled={uploading}
                      data-testid="task-document-upload"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleUpload(f);
                        e.target.value = "";
                      }}
                    />
                  </div>
                  {uploading ? (
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      Subiendo…
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-500 m-0 flex items-center gap-1">
                      <Upload className="h-3 w-3" aria-hidden />
                      Máx. 8 MB. Se marca como listo para envío al cliente.
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            {!readOnly ? (
              <div className="border-t pt-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {alerts.some((a) => ["pending", "sent"].includes(a.status)) ? (
                    <span className="text-xs text-amber-700 flex items-center gap-1">
                      <Bell className="h-3 w-3" aria-hidden />
                      Tiene recordatorio activo
                    </span>
                  ) : null}
                  {lastSent?.sent_at ? (
                    <span className="text-xs text-slate-500">
                      Último recordatorio: {String(lastSent.sent_at).slice(0, 16)}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-7"
                    onClick={() => setReminderOpen(true)}
                  >
                    Enviar recordatorio interno
                  </Button>
                </div>
              </div>
            ) : null}

            {detail?.suggested_instance_status ? (
              <p className="text-xs text-slate-600 m-0">
                Sugerencia para instancia fiscal:{" "}
                <strong>{taskStatusLabel(detail.suggested_instance_status)}</strong> (sin aplicar
                automáticamente).
              </p>
            ) : null}

            {!readOnly ? (
              <div className="space-y-2 border-t pt-3">
                <SpellcheckTextarea
                  id="task-comment"
                  label="Comentario"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                />
                <div className="flex flex-wrap gap-2 pt-1">
                  {TASK_ADVANCE_OPTIONS.filter((o) => o.value !== "not_applicable_this_period").map((opt) => (
                    <Button
                      key={opt.value}
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => patchStatus(opt.value)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => patchStatus("not_applicable_this_period", NOT_APPLICABLE_TASK_COMMENTS[0])}
                  >
                    No aplicó este mes
                  </Button>
                </div>
              </div>
            ) : null}

            {history.length ? (
              <div className="border-t pt-3">
                <p className="font-medium mb-2">Historial</p>
                <ul className="space-y-1 text-xs text-slate-600 max-h-32 overflow-y-auto">
                  {history.map((h) => (
                    <li key={h.id}>
                      {h.created_at?.slice(0, 16)} — {h.action}
                      {h.comment ? `: ${h.comment}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
      <InternalReminderDialog
        open={reminderOpen}
        onOpenChange={setReminderOpen}
        context={
          item
            ? {
                fiscalClientId: item.fiscal_client_id,
                despachoTaskId: item.id,
                complianceInstanceId: item.compliance_instance_id,
                recipientUserId: item.assigned_to_user_id,
                recipientLabel: item.assigned_to_name || "Auxiliar asignado",
                defaultMessage: `Tarea pendiente: «${item.task_name || item.title}» de ${
                  item.client_name || "cliente"
                } vence ${item.due_date ? `el ${item.due_date}` : "pronto"}.`,
              }
            : null
        }
        onSent={loadDetail}
      />
    </Dialog>
  );
}
